import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL !== undefined
      ? process.env.REACT_APP_API_BASE_URL
      : "http://localhost:5001";

  // Set default axios config for credentials and CSRF
  axios.defaults.withCredentials = true;
  axios.defaults.xsrfCookieName = "csrf_token";
  axios.defaults.xsrfHeaderName = "X-CSRFToken";

  const checkAuth = useCallback(
    async (shouldSetLoading = true) => {
      try {
        if (shouldSetLoading) setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/me`);
        setUser(response.data);
        setError(null);
      } catch (err) {
        setUser(null);
        // Not a fatal error if it's just not logged in
      } finally {
        if (shouldSetLoading) setLoading(false);
      }
    },
    [API_BASE_URL],
  );

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/login`, {
        email,
        password,
      });
      setUser(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || "Login failed");
      throw err;
    }
  };

  const register = async (
    email,
    password,
    companyName,
    inn,
    telegramUsername,
    consent,
    consentMeta = {},
  ) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/register`, {
        email,
        password,
        company_name: companyName,
        inn,
        telegram_username: telegramUsername,
        consent,
        consent_meta: consentMeta,
      });
      setError(null);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || "Registration failed");
      throw err;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/logout`);
      setUser(null);
    } catch (err) {
      console.error("Logout failed", err);
    }
  };

  const requestPasswordReset = async (email) => {
    const response = await axios.post(
      `${API_BASE_URL}/api/password-reset/request`,
      { email },
    );
    return response.data;
  };

  const resetPassword = async (token, password) => {
    await axios.post(`${API_BASE_URL}/api/password-reset/confirm`, {
      token,
      password,
    });
  };

  /** Личная ссылка t.me для подключения уведомлений. */
  const getTelegramLink = async () => {
    const response = await axios.post(`${API_BASE_URL}/api/telegram/link`);
    return response.data.url;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        logout,
        checkAuth,
        requestPasswordReset,
        resetPassword,
        getTelegramLink,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
